/* トップページのヒーロースライダー
 *
 * 中央のバナーを大きく見せ、前後を端に少し覗かせる。覗いている側はCSSでぼかす。
 * スライド幅も隙間も設定で変わるため、位置合わせは％ではなく実寸から計算する。
 *   ずらす量 = 対象スライドの中心 － 表示枠の中心
 *
 * 端まで行ったら先頭へ戻る「無限ループ」は、前後にクローンを1枚ずつ足して実現している。
 * 実体の並びだけで戻すと全スライドを逆走してしまうため、クローンまで進めてから
 * アニメーションを切って一瞬で実体側へ座標を移す。
 *
 * 自動送りを止める条件
 *   ・マウスが乗っている / キーボードのフォーカスが中にある
 *   ・タブが裏に回っている
 *   ・OSの「視差効果を減らす」が有効
 */
class HeroCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.hero-carousel__viewport');
    this.track = this.querySelector('.hero-carousel__track');
    this.count = this.querySelectorAll('.hero-carousel__slide').length;
    if (!this.track || this.count === 0) return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.count === 1) {
      this.classList.add('hero-carousel--single');
      this.querySelector('.hero-carousel__slide').classList.add('is-active');
      return;
    }

    this._buildClones();
    this.slides = Array.from(this.querySelectorAll('.hero-carousel__slide'));
    this.index = 1; // 実体1枚目（0番はクローン）

    this.dots = Array.from(this.querySelectorAll('.hero-carousel__dot'));
    this.interval = Number(this.dataset.interval || 5000);
    this.autoplay = this.dataset.autoplay === 'true' && !this.reduceMotion;

    this._bind();
    this._sync();

    // 画像の読み込みで幅が確定してからでないと位置がずれる
    requestAnimationFrame(() => this._apply(false));
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this._apply(false));
    }
    window.addEventListener('load', () => this._apply(false), { once: true });

    this._start();
  }

  disconnectedCallback() {
    this._stop();
    if (this._ro) this._ro.disconnect();
  }

  _buildClones() {
    const all = Array.from(this.querySelectorAll('.hero-carousel__slide'));
    const first = all[0].cloneNode(true);
    const last = all[all.length - 1].cloneNode(true);
    for (const el of [first, last]) {
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute('tabindex', '-1');
      const img = el.querySelector('img');
      if (img) img.setAttribute('loading', 'lazy');
    }
    this.track.appendChild(first);
    this.track.insertBefore(last, this.track.firstChild);
  }

  /* 対象スライドの中心を、表示枠の中心に合わせる */
  _offsetFor(i) {
    const slide = this.slides[i];
    if (!slide) return 0;
    return slide.offsetLeft + slide.offsetWidth / 2 - this.viewport.clientWidth / 2;
  }

  _apply(animate) {
    this.track.classList.toggle('hero-carousel__track--animate', animate && !this.reduceMotion);
    this.track.style.transform = `translate3d(${-this._offsetFor(this.index)}px, 0, 0)`;
  }

  go(step) {
    if (this.moving) return;
    this.moving = true;
    this.index += step;
    this._apply(true);
    this._sync();
  }

  goTo(realIndex) {
    if (this.moving) return;
    const target = realIndex + 1;
    if (target === this.index) return;
    this.moving = true;
    this.index = target;
    this._apply(true);
    this._sync();
  }

  /* クローンの上で止まったら、対応する実体へ瞬間移動する */
  _onEnd() {
    this.moving = false;
    if (this.index === 0) this.index = this.count;
    else if (this.index === this.count + 1) this.index = 1;
    else return;
    this._apply(false);
    void this.track.offsetWidth; // 直後に transition を戻しても再生されないように
    this._sync();
  }

  get realIndex() {
    return (this.index - 1 + this.count) % this.count;
  }

  /* 中央のスライドだけ鮮明にし、ドットを合わせる */
  _sync() {
    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.index));
    const cur = this.realIndex;
    this.dots.forEach((d, i) => d.setAttribute('aria-current', i === cur ? 'true' : 'false'));
  }

  _bind() {
    this.track.addEventListener('transitionend', (e) => {
      if (e.propertyName === 'transform') this._onEnd();
    });

    const prev = this.querySelector('.hero-carousel__arrow--prev');
    const next = this.querySelector('.hero-carousel__arrow--next');
    if (prev) prev.addEventListener('click', () => { this._stop(); this.go(-1); this._start(); });
    if (next) next.addEventListener('click', () => { this._stop(); this.go(1); this._start(); });

    this.dots.forEach((d, i) => {
      d.addEventListener('click', () => { this._stop(); this.goTo(i); this._start(); });
    });

    // 端に覗いているバナーを押したときは、リンクへ飛ばずにそれを中央へ持ってくる。
    // ぼけて読めない状態のまま遷移させられると、押し間違いと区別がつかない。
    this.slides.forEach((s, i) => {
      s.addEventListener('click', (e) => {
        if (i === this.index) return;
        e.preventDefault();
        this._stop();
        if (i === 0) this.go(-1);
        else if (i === this.count + 1) this.go(1);
        else this.goTo(i - 1);
        this._start();
      });
    });

    this.addEventListener('mouseenter', () => this._stop());
    this.addEventListener('mouseleave', () => this._start());
    this.addEventListener('focusin', () => this._stop());
    this.addEventListener('focusout', () => {
      if (!this.contains(document.activeElement)) this._start();
    });

    document.addEventListener('visibilitychange', () => {
      document.hidden ? this._stop() : this._start();
    });

    // 幅が変わると中心がずれるので、そのつど計算し直す
    if (window.ResizeObserver) {
      this._ro = new ResizeObserver(() => {
        clearTimeout(this._rt);
        this._rt = setTimeout(() => this._apply(false), 120);
      });
      this._ro.observe(this.viewport);
    }

    this._bindSwipe();
  }

  _bindSwipe() {
    let x0 = null;
    let y0 = null;
    let decided = false;

    this.addEventListener('touchstart', (e) => {
      const t = e.changedTouches[0];
      x0 = t.clientX;
      y0 = t.clientY;
      decided = false;
      this._stop();
    }, { passive: true });

    this.addEventListener('touchmove', (e) => {
      if (x0 === null || decided) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - x0;
      const dy = t.clientY - y0;
      // 縦の動きが勝っていればページのスクロールとして扱う
      if (Math.abs(dy) > Math.abs(dx)) { x0 = null; return; }
      if (Math.abs(dx) > 40) {
        decided = true;
        this.go(dx < 0 ? 1 : -1);
      }
    }, { passive: true });

    const end = () => { x0 = null; this._start(); };
    this.addEventListener('touchend', end, { passive: true });
    this.addEventListener('touchcancel', end, { passive: true });
  }

  _start() {
    if (!this.autoplay || this.timer) return;
    this.timer = setInterval(() => this.go(1), this.interval);
  }

  _stop() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('hero-carousel')) {
  customElements.define('hero-carousel', HeroCarousel);
}
